import type { ReactNode } from "react";
import { ChevronRight } from "lucide-react";
import { cn } from "@/lib/cn";

export function SettingsPanel({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: ReactNode;
}) {
  return (
    <div className="flex h-full min-h-0 flex-col overflow-y-auto bg-surface-muted/40">
      <div className="sticky top-0 z-10 border-b border-border bg-surface px-6 py-4">
        <h3 className="text-lg font-semibold text-fg">{title}</h3>
        {description ? <p className="mt-0.5 text-sm text-fg-muted">{description}</p> : null}
      </div>
      <div className="mx-auto w-full max-w-2xl space-y-4 px-6 py-6">{children}</div>
    </div>
  );
}

export function SettingsCard({
  title,
  description,
  children,
  className,
}: {
  title?: string;
  description?: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={cn("overflow-hidden rounded-[6px] border border-border bg-surface", className)}>
      {title ? (
        <div className="border-b border-border px-4 py-3">
          <h4 className="text-sm font-semibold text-fg">{title}</h4>
          {description ? <p className="mt-0.5 text-xs text-fg-muted">{description}</p> : null}
        </div>
      ) : null}
      <div className="divide-y divide-border">{children}</div>
    </section>
  );
}

export function SettingsRow({
  title,
  description,
  trailing,
  onClick,
  danger,
}: {
  title: string;
  description?: string;
  trailing?: ReactNode;
  onClick?: () => void;
  danger?: boolean;
}) {
  const content = (
    <>
      <span className="min-w-0 flex-1">
        <span className={cn("block text-sm font-medium", danger ? "text-danger" : "text-fg")}>
          {title}
        </span>
        {description ? (
          <span className="mt-0.5 block text-xs text-fg-muted">{description}</span>
        ) : null}
      </span>
      {trailing ??
        (onClick ? <ChevronRight className="h-4 w-4 shrink-0 text-fg-subtle" /> : null)}
    </>
  );

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className="flex w-full items-center gap-3 px-4 py-3.5 text-left transition-colors hover:bg-surface-muted/70"
      >
        {content}
      </button>
    );
  }

  return <div className="flex items-center gap-3 px-4 py-3.5">{content}</div>;
}

export function SettingsToggle({
  title,
  description,
  checked,
  onChange,
}: {
  title: string;
  description?: string;
  checked: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className="flex w-full items-center gap-3 px-4 py-3.5 text-left hover:bg-surface-muted/40"
    >
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-medium text-fg">{title}</span>
        {description ? (
          <span className="mt-0.5 block text-xs text-fg-muted">{description}</span>
        ) : null}
      </span>
      <span
        className={cn(
          "relative h-6 w-11 shrink-0 rounded-full transition-colors",
          checked ? "bg-papaya-deep" : "bg-surface-muted ring-1 ring-border",
        )}
      >
        <span
          className={cn(
            "absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white shadow-sm transition-transform",
            checked && "translate-x-5",
          )}
        />
      </span>
    </button>
  );
}

export function SettingsSelectRow({
  title,
  description,
  value,
  options,
  onChange,
}: {
  title: string;
  description?: string;
  value: string;
  options: Array<{ value: string; label: string }>;
  onChange: (value: string) => void;
}) {
  return (
    <div className="flex items-center gap-3 px-4 py-3.5">
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-medium text-fg">{title}</span>
        {description ? (
          <span className="mt-0.5 block text-xs text-fg-muted">{description}</span>
        ) : null}
      </span>
      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="h-9 rounded-[6px] border border-border bg-surface px-2 text-sm text-fg outline-none focus-visible:ring-2 focus-visible:ring-ring/30"
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
}

export function SettingsField({
  label,
  value,
  hint,
  onChange,
  placeholder,
  readOnly,
}: {
  label: string;
  value: string;
  hint?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  readOnly?: boolean;
}) {
  return (
    <div className="px-4 py-3.5">
      <label className="mb-1.5 block text-xs font-medium text-fg-subtle">{label}</label>
      <input
        value={value}
        readOnly={readOnly}
        placeholder={placeholder}
        onChange={(event) => onChange?.(event.target.value)}
        className={cn(
          "h-10 w-full rounded-[6px] border border-border bg-surface px-3 text-sm text-fg outline-none placeholder:text-fg-subtle focus-visible:ring-2 focus-visible:ring-ring/30",
          readOnly && "bg-surface-muted text-fg-muted",
        )}
      />
      {hint ? <p className="mt-1.5 text-xs text-fg-muted">{hint}</p> : null}
    </div>
  );
}

export function SettingsNote({ children }: { children: ReactNode }) {
  return (
    <p className="rounded-[6px] border border-border bg-surface px-4 py-3 text-xs leading-relaxed text-fg-muted">
      {children}
    </p>
  );
}
